// *** Angular imports
import { NgIf, NgTemplateOutlet } from '@angular/common';
import {
    AfterViewInit, ChangeDetectorRef, Component, ContentChild,
    ElementRef, HostListener, Input, OnDestroy, OnInit, Renderer2, TemplateRef,
    forwardRef
} from '@angular/core';

// *** rxjs imports
import { Subject } from 'rxjs';

// *** Telerik imports
import { ButtonsModule } from "@progress/kendo-angular-buttons";
import { IndicatorsModule } from "@progress/kendo-angular-indicators";
import { InputsModule } from '@progress/kendo-angular-inputs';
import { SVGIcon, fileExcelIcon, filterIcon, searchIcon, xCircleIcon } from "@progress/kendo-svg-icons";

// *** Framework imports
import { ControlResizerOptions, ControlResizerService } from '../../../../../services/framework';
import { CriteriaContainerComponent, CriteriaSummaryBox, QueryPanelFilterBoxComponent } from 'src/app/modules/fwcore/components';
import { QueryPanelConnectorService, QueryPanelContentConnectorService } from 'src/app/modules/fwcore/services';
import { ICriteriaComponent } from 'src/app/modules/fwcore/models';

@Component({
  selector: 'query-panel',
  standalone: true,
  styleUrls: ['./query-panel.component.scss'],
  templateUrl: './query-panel.component.html',
  imports: [ButtonsModule, IndicatorsModule, InputsModule, NgIf, NgTemplateOutlet
    , CriteriaSummaryBox, CriteriaContainerComponent, forwardRef(()=> QueryPanelFilterBoxComponent)]
})

export class QueryPanelComponent
  implements OnDestroy, OnInit, AfterViewInit {
  /*-------------------------- Input / Outputs --------------------------------*/

  @Input() get caption(): string { return this._caption; }
  @Input() contentTemplate!: TemplateRef<any> | null;
  @Input() toolbarTemplate!: TemplateRef<any> | null;

  public set caption(caption: string) {
    this._caption = caption;
  }

  /*-------------------------- Inputs / Outputs --------------------*/

  @Input() public queryOnLoad: boolean = true;
  // buttons
  @Input() public showHeaderExcelButton: boolean = true;
  @Input() public showHeaderOpenCloseButton: boolean = true;
  @Input() public showHeaderSearchButton: boolean = false;
  @Input() public searchButtonTooltip: string = "Search";
  @Input() public searchCaption: string = "Apply filters";
  @Input() public set showCriteriaPanel(val: boolean) {
    if (val) {
      this.gridClass = "grid";
      this.loadCriteria();
    } else {
      this.gridClass = "gHide";
    }
  }
  /*-------------------------- Internal properties  ---------------------------*/

  @ContentChild('criteria') criteriaComponent!: ICriteriaComponent;
  @ContentChild(CriteriaContainerComponent) treeViewContainer:
    CriteriaContainerComponent | undefined;

  protected destroy$ = new Subject();
  protected gridClass: string = "gHide";
  protected gridWidth: number = 100;
  protected paneHeight: number = 640;
  protected showCriteriaSummary: boolean = false;
  protected viewInit: boolean = false;

  // svg icons
  protected excelSvg: SVGIcon = fileExcelIcon;
  protected filterSvg: SVGIcon = filterIcon;
  protected searchSvg: SVGIcon = searchIcon;
  protected xCircleSvg: SVGIcon = xCircleIcon;

  protected subscription: any;

  //private interval?: any;
  private unlistener: () => void;
  protected _caption: string = "";

  /*-------------------------- Constructor  --------------------------*/

  constructor (
    private _cdr: ChangeDetectorRef,
    protected controlResizer: ControlResizerService,
    protected criteriaService: QueryPanelConnectorService,
    protected contentConnector: QueryPanelContentConnectorService,
    protected elem: ElementRef,
    private renderer: Renderer2
  ) {
    this.unlistener = function () {};
  }

  /*----------------------------------- ng  --------------------------*/

  ngOnInit(): void {
    this.viewInit = true;
    this.showCriteriaSummary = this.criteriaService.hasObservers();

    // hide loading panel
    this.subscription = this.criteriaService.onMessage().subscribe(message => {
      if (message) {
        switch (message.type) {
          case "isLoaded":
            --this._loadCount;
            if (this._loadCount < 1) {
              this.loading = false;
              this._cdr.detectChanges();
            }
            break;
          case "load":
            this.criteriaService.isLoaded("_");
            break;
        }
      }
    });

    this.sizeChange();
  }

  ngAfterViewInit(): void {
    var topBarMenu = document.getElementsByClassName("topbar-menu");
    if (topBarMenu != undefined && topBarMenu.length > 0) {
      this.unlistener = this.renderer.listen(topBarMenu[0], "click",
        event => {
          this.sizeChange();
        });
    }
    // send a first search request
    if (this.queryOnLoad) {
      this.onSearchRequest();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.unlistener();
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  /*--------------------------- text filter --------------------------*/

  // change event for the filter text box (might not be needed?)
  protected onFilterChange(value: string) {
    this.criteriaComponent.textFilter = value;
    this.onSearchRequest();
  }

  /*--------------------------- buttons --------------------------*/

  protected onClearClick() {
    this.criteriaService.clearItems();
  }

  // click event for the "show / hide filter" button
  protected onFilterShowHide(e: boolean) {
    // show on true, hide on false
    if (e) {
      this.gridClass = "grid";
      this.loadCriteria();
    } else {
      this.gridClass = "gHide";
    }
    this.sizeChange();
  }

  private areCriteriaValid(): boolean {
    if (this.criteriaComponent && this.criteriaComponent.isValid()) {
      return true;
    }

    // TODO: a better alert than this
    alert("One or more required search criteria are missing, or not valid.");
    return false;
  }
  protected onExportRequest() {
    if (this.areCriteriaValid()) {
      var params = this.criteriaComponent.getParameters();
      var headerLines = this.criteriaComponent.getHeaderLines();
      this.contentConnector.export(params, this._caption, headerLines);
    }
  }

  protected onSearchRequest() {
    if (this.areCriteriaValid()) {
      var criteria = this.criteriaComponent.getParameters();
      this.contentConnector.search(criteria);
    }
  }

  /*--------------------------- lazy loading --------------------------*/
  // lazy loading
  protected loading: boolean = false;
  private _loadCount: number = -1;

  protected loadCriteria(): void {
    if (this._loadCount < 0) {
      this._loadCount = this.criteriaService.observerCount;
      this.loading = (this._loadCount > 0);
      this.criteriaService.loadAll();
    }
  }

  /*--------------------------- resize --------------------------*/

  @HostListener('window:resize', ['$event'])
  protected onResize(event: Event) {
    this.sizeChange();
  }

  protected sizeChange() {
    if (this.viewInit) {
      // calculate width
      if (this.elem.nativeElement) {
        // Get gap between menu panel and query panel
        var offsetLeft = this.elem.nativeElement.offsetLeft;

        // Get width of menu panel
        var menuOffset = 0;
        var menuPanel = document.querySelector(".isgmenupanel");
        if (menuPanel != undefined && menuPanel != null) {
          menuOffset = menuPanel.getBoundingClientRect().width;
        }

        // Get width of the right hand side
        var rightPanel = this.elem.nativeElement.querySelector(".right");
        var rightWidth = 0;
        if (this.gridClass == "grid" && rightPanel != undefined && rightPanel != null) {
          rightWidth = 330; // rightPanel.getBoundingClientRect().width;
        }

        // Calculate the width of the grid
        this.gridWidth = window.innerWidth - (offsetLeft * 2) - menuOffset - rightWidth - 109;

        // calculate maximum height of the right hand panel
        var options: ControlResizerOptions = new ControlResizerOptions();
        options.offsetTopHeight = 54;
        options.offsetBottomHeight = 80;
        this.paneHeight = this.controlResizer.calculateHeight(options);
      }
    }
  }
}
